import React, { useMemo, useRef, useState } from 'react';
import type { ApprovalItem } from '../client';
import { ApprovalCardHeader, relativeTime } from './ApprovalCardHeader';
import { ApprovalField, ApprovalInputs } from './ApprovalInputs';
import AsyncButton from '../components/AsyncButton';

export { relativeTime };

type DecisionPending = 'approved' | 'rejected' | null;

export const ApprovalCard: React.FC<{
  approval: ApprovalItem;
  deciding?: boolean;
  onDecide: (
    decision: 'approved' | 'rejected',
    note: string,
    inputs?: Record<string, unknown>,
  ) => Promise<void>;
}> = ({ approval, deciding, onDecide }) => {
  const [note, setNote] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [decisionPending, setDecisionPending] = useState<DecisionPending>(null);
  const decisionInFlight = useRef(false);
  const fields = ((approval.detail?.fields || []) as ApprovalField[]).filter(
    (field) => field?.key,
  );
  const missingRequiredInput = fields.some(
    (field) => field.secret && !(values[field.key] ?? field.value ?? '').trim(),
  );
  const inputs = useMemo(() => {
    if (fields.length === 0) return undefined;
    const credentials: Record<string, string> = {};
    const config: Record<string, string> = {};
    fields.forEach((field) => {
      const value = (values[field.key] ?? field.value ?? '').trim();
      if (!value) return;
      if (field.secret) credentials[field.key] = value;
      else config[field.key] = value;
    });
    return {
      credentials,
      config,
    };
  }, [fields, values]);
  const origin = approval.origin;
  const isProjectCard =
    origin?.kind === 'project_card' && !!origin.projectId && !!origin.cardId;

  const decide = async (decision: 'approved' | 'rejected') => {
    if (decisionInFlight.current) return;
    decisionInFlight.current = true;
    setDecisionPending(decision);
    try {
      await onDecide(
        decision,
        note.trim(),
        decision === 'approved' ? inputs : undefined,
      );
      setNote('');
      setValues({});
    } finally {
      decisionInFlight.current = false;
      setDecisionPending(null);
    }
  };

  return (
    <article
      className={`approval-card ${isProjectCard ? 'approval-card-project' : 'approval-card-chat'}`}
      data-testid={`approval-${approval.id}`}>
      <ApprovalCardHeader
        teamId={approval.teamId}
        agent={approval.requestingAgent}
        requestingUser={approval.requestingUser}
        requestingTrigger={approval.requestingTrigger}
        origin={origin}
        createdAt={approval.createdAt}
        title={approval.title}
      />
      <div className="approval-reason">
        <span>The agent's stated reason</span>
        <p>
          {approval.reason ||
            (approval.legacyReasonUnavailable
              ? 'Reason unavailable for this older request.'
              : '')}
        </p>
      </div>

      {fields.length > 0 && (
        <ApprovalInputs
          fields={fields}
          values={values}
          disabled={deciding || decisionPending !== null}
          onChange={setValues}
        />
      )}

      <label className="approval-note">
        <span>
          Note to agent <small>(optional)</small>
        </span>
        <textarea
          aria-label="Note to agent"
          maxLength={4000}
          disabled={deciding || decisionPending !== null}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Add context, constraints, or next steps…"
        />
      </label>
      <div className="approval-actions">
        <AsyncButton
          type="button"
          className="approval-reject"
          disabled={deciding || decisionPending !== null}
          loading={decisionPending === 'rejected'}
          loadingText="Rejecting..."
          onClick={() => decide('rejected')}>
          Reject
        </AsyncButton>
        <AsyncButton
          type="button"
          className="approval-approve"
          disabled={
            deciding || decisionPending !== null || missingRequiredInput
          }
          loading={decisionPending === 'approved'}
          loadingText="Approving..."
          onClick={() => decide('approved')}>
          Approve
        </AsyncButton>
      </div>
    </article>
  );
};
